import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState, HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { getHdPlanetsBySection, HdPlanetDisplayConfig } from '../hd-planet-display-config';
import { findActivationFromList, isGateInDefinedCenter } from '../hd-planet-grid.utils';
import { HdPlanetActivationBlockClickEvent, HdPlanetActivationBlockComponent, HdPlanetGridDensity } from '../hd-planet-activation-block/hd-planet-activation-block.component';

interface HdLpsBlockViewModel {
  activation: HdBodygraphPlanetaryActivation;
  displayConfig: HdPlanetDisplayConfig;
  isDefined: boolean;
  geneKeysLabel: string | null;
}

/**
 * The 3×3 Lunar Planetary Square grid: Moon through Pluto.
 * Each cell shows poort.kleur.lijn (variant='grid').
 *
 * For personality the grid reads left-to-right.
 * For design (mirrored) the grid reads right-to-left per row.
 */
@Component({
  selector: 'hd-lunar-planetary-square',
  templateUrl: './hd-lunar-planetary-square.component.html',
  styleUrl: './hd-lunar-planetary-square.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdPlanetActivationBlockComponent],
})
export class HdLunarPlanetarySquareComponent {
  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly side = input.required<HdBodygraphSide>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  readonly showGeneKeys = input(false);
  readonly showHdKeynotes = input(false);
  /** Mini-densiteit (mobiel): compacte twee-regel-blokken — zie {@link HdPlanetGridDensity}. */
  readonly density = input<HdPlanetGridDensity>('normal');
  /** Line numbers from opposite-side nodes, forwarded to each activation block for yellow line highlighting. */
  readonly matchLines = input<ReadonlySet<number>>(new Set());
  /** Line numbers of the profile (Sun/Earth) from both sides, forwarded for green line highlighting. */
  readonly profileLines = input<ReadonlySet<number>>(new Set());
  readonly blockClicked = output<HdPlanetActivationBlockClickEvent>();

  private readonly lpsConfigs = getHdPlanetsBySection('lunarPlanetarySquare');

  protected readonly rows = computed<HdLpsBlockViewModel[][]>(() => {
    const blocks = this.lpsConfigs
      .map(config => this.createBlockViewModel(config))
      .filter((block): block is HdLpsBlockViewModel => block !== null);

    const row1 = blocks.filter(b => b.displayConfig.lpsRow === 1);
    const row2 = blocks.filter(b => b.displayConfig.lpsRow === 2);
    const row3 = blocks.filter(b => b.displayConfig.lpsRow === 3);

    return [row1, row2, row3];
  });

  protected readonly isMirrored = computed(() => this.side() === 'design');

  private createBlockViewModel(config: HdPlanetDisplayConfig): HdLpsBlockViewModel | null {
    const activation = findActivationFromList(this.activations(), this.side(), config.planetId);

    if (!activation) {
      return null;
    }

    const geneKeysLabel = this.showGeneKeys()
      ? config.geneKeysLabels[this.side()] ?? null
      : null;

    return {
      activation,
      displayConfig: config,
      isDefined: isGateInDefinedCenter(activation.gate, this.renderState()),
      geneKeysLabel,
    };
  }
}
